/**
 * Password validation utilities
 * Shared across auth and setup flows for consistent password strength requirements
 */
/**
 * Password validation requirements (regex patterns and thresholds)
 */
const PASSWORD_REQUIREMENTS = {
    minLength: 8,
    hasUppercase: /[A-Z]/,
    hasLowercase: /[a-z]/,
    hasNumber: /\d/,
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/,
};
/**
 * Get detailed password check results for displaying requirement status in UI
 * @param password - Password to validate
 * @returns Object with each requirement check result
 */
function getPasswordChecks(password) {
    return {
        minLength: password.length >= PASSWORD_REQUIREMENTS.minLength,
        hasUppercase: PASSWORD_REQUIREMENTS.hasUppercase.test(password),
        hasLowercase: PASSWORD_REQUIREMENTS.hasLowercase.test(password),
        hasNumber: PASSWORD_REQUIREMENTS.hasNumber.test(password),
        hasSpecialChar: PASSWORD_REQUIREMENTS.hasSpecialChar.test(password),
    };
}
/**
 * Check if password meets all requirements
 * @param password - Password to validate
 * @returns true if all requirements are met
 */
function isPasswordValid(password) {
    const checks = getPasswordChecks(password);
    return Object.values(checks).every(Boolean);
}
/**
 * Validate password for react-hook-form
 * Returns error message string if invalid, or true if valid
 * @param password - Password to validate
 * @returns Error message string or true
 */
function validatePasswordForForm(password) {
    if (!password || password.trim() === '') {
        return 'Password is required';
    }
    const checks = getPasswordChecks(password);
    if (!checks.minLength) {
        return 'Password must be at least 8 characters';
    }
    if (!checks.hasUppercase) {
        return 'Password must contain at least one uppercase letter';
    }
    if (!checks.hasLowercase) {
        return 'Password must contain at least one lowercase letter';
    }
    if (!checks.hasNumber) {
        return 'Password must contain at least one number';
    }
    if (!checks.hasSpecialChar) {
        return 'Password must contain at least one special character';
    }
    return true;
}

export { PASSWORD_REQUIREMENTS, getPasswordChecks, isPasswordValid, validatePasswordForForm };
//# sourceMappingURL=passwordValidation.js.map
